
import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { AppContext } from '../context/AppContext';
import { useTranslation } from 'react-i18next';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';
import * as XLSX from 'xlsx';


// Opens a popup window with a stock inventory report
function openInventoryPrintPopup(stocks, depos, branch, physicalQty, wasteFilterType, wasteStartDate, wasteEndDate, wasteLogs = []) {
    const branchLabel = branch === 'Global' ? 'Consolidated Master Inventory' : (depos.find(d => d.id == branch)?.name || branch);

    // Main Stock Rows
    const dataRows = stocks.map((s, i) => {
        const sys = Number(s.quantity);
        const phy = physicalQty[s.id] !== undefined ? Number(physicalQty[s.id]) : sys;
        const waste = Number(s.waste_quantity || 0);
        const totalWithWaste = phy + waste;
        const lossPercent = totalWithWaste > 0 ? (waste / totalWithWaste) * 100 : 0;
        const diff = phy - sys;
        const diffColor = diff === 0 ? '#6b7280' : diff > 0 ? '#2563eb' : '#dc2626';

        // Choose price and total based on role or preference (showing actual price here)
        const displayPrice = parseFloat(s.selling_price || 0);

        return `<tr style="background:${i % 2 === 0 ? '#f8fafc' : '#fff'}">
            <td style="padding:5px 8px;font-weight:700">${s.product_name}</td>
            <td style="padding:5px 8px;font-family:monospace;font-size:9px;color:#9ca3af">${s.sku || '—'}</td>
            <td style="padding:5px 8px;font-family:monospace;font-size:9px;color:#9ca3af">${s.barcode || '—'}</td>
            <td style="padding:5px 8px;text-align:center;font-weight:700;color:#1d4ed8">${sys}</td>
            <td style="padding:5px 8px;text-align:center;font-weight:700;color:#059669">${phy}</td>
            <td style="padding:5px 8px;text-align:center;font-weight:700;color:${diffColor}">${diff > 0 ? '+' : ''}${diff}</td>
            <td style="padding:5px 8px;text-align:center;font-weight:700;color:#ef4444">${waste}</td>
            <td style="padding:5px 8px;text-align:center;font-weight:900;color:#f97316">${lossPercent.toFixed(1)}%</td>
            <td style="padding:5px 8px;text-align:right">${displayPrice.toFixed(2)}</td>
            <td style="padding:5px 8px;text-align:right;font-weight:700">${(phy * displayPrice).toFixed(2)} CFA</td>
            <td style="padding:5px 8px;text-align:center;font-weight:700;color:${sys === phy ? '#059669' : '#dc2626'}">${sys === phy ? 'Vrai' : 'Faux'}</td>
        </tr>`;
    }).join('');

    // Waste Log Rows
    const wasteRows = wasteLogs.length > 0 ? wasteLogs.map((w, i) => {
        const loss = parseFloat(w.loss_cost || 0);
        return `<tr style="background:${i % 2 === 0 ? '#fff5f5' : '#fff'}">
            <td style="padding:5px 8px;font-weight:700">${w.product_name}</td>
            <td style="padding:5px 8px;text-align:center">${w.quantity}</td>
            <td style="padding:5px 8px;text-align:center;color:#b91c1c">${w.reason}</td>
            <td style="padding:5px 8px;text-align:right;font-weight:700">CFA${loss.toFixed(2)}</td>
            <td style="padding:5px 8px;text-align:right;color:#dc2626">CFA${(loss * 0.19).toFixed(2)}</td>
            <td style="padding:5px 8px;text-align:right;color:#1e40af">CFA${(loss * 0.81).toFixed(2)}</td>
        </tr>`;
    }).join('') : '<tr><td colspan="6" style="padding:20px;text-align:center;color:#9ca3af;font-style:italic">No waste logs for this period.</td></tr>';

    const totalSys = stocks.reduce((sum, s) => sum + Number(s.quantity), 0);
    const totalPhy = stocks.reduce((sum, s) => sum + (physicalQty[s.id] !== undefined ? Number(physicalQty[s.id]) : Number(s.quantity)), 0);
    const totalWasteCount = stocks.reduce((sum, s) => sum + Number(s.waste_quantity || 0), 0);
    const totalVal = stocks.reduce((sum, s) => { const phy = physicalQty[s.id] !== undefined ? Number(physicalQty[s.id]) : Number(s.quantity); return sum + phy * parseFloat(s.selling_price || 0); }, 0);
    const totalWasteVal = wasteLogs.reduce((sum, w) => sum + parseFloat(w.loss_cost || 0), 0);
    const subTotal = totalVal - totalWasteVal;
    const deduction19 = subTotal * 0.19;
    const subTotalAfterDeduction = subTotal - deduction19;

    const periodLabel = wasteFilterType === 'today' ? 'Today' : wasteFilterType === 'month' ? 'This Month' : wasteFilterType === 'range' ? `${wasteStartDate} to ${wasteEndDate}` : 'All Time';

    const html = `<!DOCTYPE html><html><head>
        <meta charset="utf-8"><title>Stock Inventory Report — ${branchLabel}</title>
        <style>* { box-sizing: border-box; margin: 0; padding: 0; } body { font-family: Arial, sans-serif; font-size: 11px; color: #111; padding: 20px; } @page { size: A4 landscape; margin: 10mm; }
        table { border-collapse: collapse; width: 100%; margin-bottom: 20px; } th { padding: 8px; font-size: 10px; font-weight: 900; text-transform: uppercase; color: #6b7280; background: #f9fafb; text-align: left; border-bottom: 2px solid #1f2937; }
        td { border-bottom: 1px solid #f1f5f9; }
        .section-title { font-size: 14px; font-weight: 900; color: #1e3a8a; margin: 20px 0 10px 0; text-transform: uppercase; border-left: 4px solid #1e3a8a; padding-left: 10px; }
        </style>
    </head><body>
        <div style="display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #312e81;padding-bottom:10px;margin-bottom:14px">
            <div>
                <div style="font-size:20px;font-weight:900;text-transform:uppercase;color:#312e81">STOCK INVENTORY REPORT</div>
                <div style="font-size:10px;color:#6b7280;margin-top:2px;font-weight:700;text-transform:uppercase">Warehouse Management System</div>
            </div>
            <div style="text-align:right;font-size:10px;color:#6b7280">
                <div>Location: <b>${branchLabel}</b></div>
                <div>Period: <b>${periodLabel}</b></div>
                <div>Generated: <b>${new Date().toLocaleString()}</b></div>
            </div>
        </div>

        <div class="section-title">Current Inventory Levels</div>
        <div className="w-full overflow-x-auto max-w-full">
<table>
            <thead><tr>
                <th>Product Name</th><th>Reference</th><th>Barcode</th>
                <th style="text-align:center">Qty (Sys)</th><th style="text-align:center">Qty (Phy)</th>
                <th style="text-align:center">Diff</th><th style="text-align:center">Waste</th><th style="text-align:center">Loss %</th><th style="text-align:right">Price</th>
                <th style="text-align:right">Total Value</th><th style="text-align:center">Status</th>
            </tr></thead>
            <tbody>${dataRows}</tbody>
            <tfoot><tr style="background:#eef2ff;font-weight:900;border-top:2px solid #312e81">
                <td colspan="3" style="padding:8px;font-weight:900;color:#312e81">SUMMARY TOTALS</td>
                <td style="padding:8px;text-align:center">${totalSys}</td>
                <td style="padding:8px;text-align:center">${totalPhy}</td>
                <td style="padding:8px;text-align:center;color:${totalPhy - totalSys < 0 ? '#dc2626' : '#059669'}">${totalPhy > totalSys ? '+' : ''}${totalPhy - totalSys}</td>
                <td style="padding:8px;text-align:center;color:#ef4444">${totalWasteCount}</td>
                <td style="padding:8px;text-align:center;color:#f97316">${((totalWasteCount / (totalPhy + totalWasteCount || 1)) * 100).toFixed(1)}%</td>
                <td></td>
                <td style="padding:8px;text-align:right;font-weight:900;color:#312e81">${totalVal.toFixed(2)} CFA</td>
                <td></td>
            </tr></tfoot>
        </table>
</div>

        <div class="section-title">Detailed Waste / Loss Log</div>
        <div className="w-full overflow-x-auto max-w-full">
<table style="border:1px solid #fee2e2">
            <thead><tr style="background:#fef2f2">
                <th>Product Name</th><th style="text-align:center">Qty Lost</th><th style="text-align:center">Reason</th><th style="text-align:right">Loss Value</th><th style="text-align:right">19% Deduct</th><th style="text-align:right">Sub Price</th>
            </tr></thead>
            <tbody>${wasteRows}</tbody>
        </table>
</div>

        <div style="display:flex;justify-content:flex-end;margin-top:30px">
            <div style="width:350px;background:#f8fafc;border:1px solid #e2e8f0;border-left:5px solid #1e40af;padding:15px;border-radius:8px">
                <div style="display:flex;justify-content:space-between;margin-bottom:8px;font-size:10px;text-transform:uppercase;color:#475569;font-weight:700"><span>Gross Stock Value:</span><span style="color:#111;font-weight:900">CFA${totalVal.toFixed(2)}</span></div>
                <div style="display:flex;justify-content:space-between;margin-bottom:8px;font-size:10px;text-transform:uppercase;color:#b91c1c;font-weight:700"><span>Total Loss (Waste):</span><span style="font-weight:900">- CFA${totalWasteVal.toFixed(2)}</span></div>
                <div style="display:flex;justify-content:space-between;margin-bottom:8px;font-size:11px;text-transform:uppercase;color:#1e40af;font-weight:900;border-top:1px solid #cbd5e1;padding-top:8px"><span>Subtotal:</span><span>CFA${subTotal.toFixed(2)}</span></div>
                <div style="display:flex;justify-content:space-between;margin-bottom:8px;font-size:10px;text-transform:uppercase;color:#dc2626;font-weight:700"><span>19% Deduction:</span><span style="font-weight:900">- CFA${deduction19.toFixed(2)}</span></div>
                <div style="display:flex;justify-content:space-between;font-size:13px;text-transform:uppercase;color:#1e3a8a;font-weight:900;border-top:2px solid #1e3a8a;padding-top:10px;margin-top:5px"><span>Net Total:</span><span>CFA${subTotalAfterDeduction.toFixed(2)}</span></div>
            </div>
        </div>

        <div style="margin-top:40px;display:flex;justify-content:space-around">
            ${['Warehouse Supervisor', 'Inventory Control'].map(l => `<div style="text-align:center;width:28%"><div style="border-bottom:1px solid #334155;height:28px;margin-bottom:4px"></div><div style="font-size:9px;color:#64748b;font-weight:700">${l}</div></div>`).join('')}
        </div>
        <div style="margin-top:20px;text-align:center;font-size:9px;color:#94a3b8">ERP System &bull; Confidential Inventory Log &bull; ${new Date().toLocaleString()}</div>
    <script>window.onload=function(){window.print();window.onafterprint=function(){window.close();};};</script>
    </body></html>`;

    const popup = window.open('', '_blank', 'width=1050,height=750,scrollbars=yes');
    if (popup) { popup.document.write(html); popup.document.close(); }
    else alert('Please allow popups for this site to print.');
}

const Inventory = () => {
    const { selectedDepo, depos, authFetch, user } = useContext(AppContext);
    const [branch, setBranch] = useState(
        (selectedDepo && selectedDepo !== 'Global') ? selectedDepo : ((depos && depos[0]?.id) || 'Global')
    );
    const { t, i18n } = useTranslation();
    const [stocks, setStocks] = useState([]);
    const [isWasteModalOpen, setIsWasteModalOpen] = useState(false);
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);

    // Waste Form State
    const [wasteForm, setWasteForm] = useState({ product_id: '', depo_id: '', quantity: '', reason: 'Expired' });

    // Import State
    const [importDepoId, setImportDepoId] = useState('');
    const [importPreview, setImportPreview] = useState([]);
    const [importFile, setImportFile] = useState(null);
    const [importResult, setImportResult] = useState(null);
    const [importLoading, setImportLoading] = useState(false);

    const fileInputRef = useRef(null);

    const [editStock, setEditStock] = useState(null);

    const fetchStocks = () => {
        const url = branch === 'Global'
            ? '/api/stock'
            : `/api/stock?depo_id=${branch}`;
        authFetch(url)
            .then(res => res.json())
            .then(data => setStocks(Array.isArray(data) ? data : []))
            .catch(console.error);
    };

    // fetchStocks is defined inside the component; we only want to re-run
    // when `selectedDepo` changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        fetchStocks();
    }, [branch]);

    const handleUpdateStock = async (e) => {
        e.preventDefault();
        try {
            await authFetch(`/api/stock/${editStock.id}`, {
                method: 'PUT',
                body: JSON.stringify(editStock)
            });
            setEditStock(null);
            fetchStocks();
        } catch (err) {
            alert(t("Failed to update"));
        }
    };

    const handleDeleteStock = async (id) => {
        if (!window.confirm(t("Are you sure?"))) return;
        try {
            await authFetch(`/api/stock/${id}`, { method: 'DELETE' });
            fetchStocks();
        } catch (err) {
            alert(t("Delete failed"));
        }
    };

    // Add state for physical quantity input and diff calculation
    const [physicalQty, setPhysicalQty] = useState({}); // { stockId: value }

    const handlePhysicalQtyChange = (stockId, value) => {
        setPhysicalQty(prev => ({ ...prev, [stockId]: value }));
    };

    const [showPrint, setShowPrint] = useState(false);
    const [showDraft, setShowDraft] = useState(false);
    const [physicalQtyDraft, setPhysicalQtyDraft] = useState(() => {
        try {
            const saved = localStorage.getItem('physicalQtyDraft');
            return saved ? JSON.parse(saved) : {};
        } catch {
            return {};
        }
    });
    const [draftHistory, setDraftHistory] = useState([]);
    const [loadingDraftHistory, setLoadingDraftHistory] = useState(false);

    const handleSaveDraft = async () => {
        // Save locally
        localStorage.setItem('physicalQtyDraft', JSON.stringify(physicalQty));
        // Save all table rows to backend as a batch
        const draftRows = stocks.map(s => {
            const sys = Number(s.quantity);
            const phy = Number(physicalQty[s.id] ?? sys);
            return {
                stock_id: s.id,
                depo_id: branch,
                old_physical_qty: sys,
                new_physical_qty: phy,
                changed_by: user?.name || user?.username || '',
                change_reason: 'Draft save from Inventory page'
            };
        });
        try {
            const res = await authFetch(`/api/stock?action=save_draft`, {
                method: 'POST',
                body: JSON.stringify({ rows: draftRows })
            });
            const data = await res.json();
            if (res.ok) {
                alert('Draft saved for branch: ' + (depos.find(d => d.id == branch)?.name || branch));
            } else {
                alert('Error saving draft: ' + (data.message || 'Unknown error'));
            }
        } catch (err) {
            alert('Network error saving draft');
        }
    };

    const handleViewDraft = async () => {
        setLoadingDraftHistory(true);
        try {
            const res = await authFetch(`/api/stock?action=draft_history&depo_id=${branch}`);
            const data = await res.json();
            setDraftHistory(Array.isArray(data) ? data : []);
        } catch {
            setDraftHistory([]);
        }
        setShowDraft(true);
        setLoadingDraftHistory(false);
    };

    const handleClearDraft = () => {
        localStorage.removeItem('physicalQtyDraft');
        setPhysicalQty({});
        setPhysicalQtyDraft({});
        alert('Draft cleared!');
    };

    const handleExport = () => {
        const exportData = stocks.map(s => {
            const row = {
                'Stock ID': s.id,
                'Name': s.product_name,
                'Reference Number': s.sku || '',
                'Barcode': s.barcode || '',
                'Depo': s.depo_name,
                'Quantity': s.quantity,
                'Unit': s.unit || 'pcs',
                'Actual Price': parseFloat(s.selling_price || 0).toFixed(2),
                'Total Value (Actual)': (s.quantity * s.selling_price).toFixed(2),
            };
            if (user?.role === 'Admin') {
                row['Cost Price'] = parseFloat(s.purchase_price).toFixed(2);
                row['Total Value (Cost)'] = (s.quantity * s.purchase_price).toFixed(2);
            }
            return row;
        });
        // Add summary row
        exportData.push({
            'Stock ID': '',
            'Name': 'TOTAL',
            'Reference Number': '',
            'Barcode': '',
            'Depo': branch === 'Global' ? 'All Branches' : (depos.find(d => d.id == branch)?.name || ''),
            'Quantity': stocks.reduce((sum, s) => sum + Number(s.quantity), 0),
            'Unit': '',
            'Actual Price': '',
            'Total Value (Actual)': stocks.reduce((sum, s) => sum + s.quantity * parseFloat(s.selling_price || 0), 0).toFixed(2),
        });
        const ws = XLSX.utils.json_to_sheet(exportData);

        // Auto-column widths
        const colWidths = Object.keys(exportData[0] || {}).map(k => ({
            wch: Math.max(k.length, ...exportData.map(r => String(r[k]).length))
        }));
        ws['!cols'] = colWidths;

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Stock');

        const fileName = `Stock_Export_${new Date().toISOString().slice(0, 10)}.xlsx`;
        XLSX.writeFile(wb, fileName);
    };

    // ─── IMPORT — parse file ─────────────────────────────────────────────────
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setImportFile(file);
        setImportResult(null);

        const reader = new FileReader();
        reader.onload = (evt) => {
            const wb = XLSX.read(evt.target.result, { type: 'binary' });
            const ws = wb.Sheets[wb.SheetNames[0]];
            const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });

            // Normalise column names to lowercase keys
            const normalised = rows.map(r => {
                const out = {};
                Object.entries(r).forEach(([k, v]) => {
                    out[k.toLowerCase().replace(/\s+/g, '_').replace(/[()]/g, '')] = v;
                });
                return out;
            });
            setImportPreview(normalised.slice(0, 5));  // show first 5 rows as preview
            setImportFile({ file, rows: normalised });
        };
        reader.readAsBinaryString(file);
    };

    // ─── IMPORT — submit ─────────────────────────────────────────────────────
    const handleImportSubmit = async () => {
        if (!importFile?.rows || !importDepoId) {
            alert('Please select a file and choose a destination depo.');
            return;
        }
        setImportLoading(true);
        setImportResult(null);

        try {
            const res = await authFetch('/api/import', {
                method: 'POST',
                body: JSON.stringify({ rows: importFile.rows, depo_id: importDepoId })
            });
            const data = await res.json();
            setImportResult(data);
            if (res.ok) fetchStocks();
        } catch (e) {
            setImportResult({ message: 'Network error.' });
        } finally {
            setImportLoading(false);
        }
    };

    const handleDownloadTemplate = () => {
        const template = [{ name: 'Example Product', barcode: '2001234567895', sku: 'REF001', category: 'Food', unit: 'kg', quantity: 50, price: 3.50 }];
        const ws = XLSX.utils.json_to_sheet(template);
        ws['!cols'] = Object.keys(template[0]).map(k => ({ wch: 18 }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Template');
        XLSX.writeFile(wb, 'Import_Template.xlsx');
    };

    const handleLogWaste = async (e) => {
        e.preventDefault();
        try {
            await authFetch('/api/waste', {
                method: 'POST',
                body: JSON.stringify(wasteForm)
            });
            alert("Waste logged. Stock auto-deducted.");
            setIsWasteModalOpen(false);
            fetchStocks(); // Refresh stocks after logging waste
        } catch (err) {
            alert("Failed to log waste");
        }
    };

    const columns = [
        { header: 'Stock ID', accessor: 'id', render: row => <span className="text-xs text-gray-400">{row.id}</span> },
        { header: 'Product', accessor: 'product_name', render: row => <span className="font-semibold">{row.product_name}</span> },
        { header: 'Reference Number', accessor: 'sku' },
        { header: 'Barcode', accessor: 'barcode', render: row => <span className="font-mono text-xs text-gray-500">{row.barcode || '—'}</span> },
        { header: 'Depo', accessor: 'depo_name' },
        { header: 'Qty (System)', accessor: 'quantity', render: row => <span className="font-bold">{row.quantity}</span> },
        {
            header: 'Qty (Physical)', accessor: 'physical_qty', render: row => (
                <input
                    type="number"
                    className="w-20 border rounded p-1 text-xs text-center"
                    value={physicalQty[row.id] ?? ''}
                    onChange={e => handlePhysicalQtyChange(row.id, e.target.value)}
                    placeholder="Enter..."
                />
            )
        },
        {
            header: 'Diff', accessor: 'diff', render: row => {
                const sys = Number(row.quantity);
                const phy = Number(physicalQty[row.id] ?? sys);
                const diff = phy - sys;
                return <span className={diff === 0 ? 'text-green-600' : diff > 0 ? 'text-blue-600' : 'text-red-600'}>{diff}</span>;
            }
        },
        { header: 'Actual Price', accessor: 'selling_price', render: row => <>{parseFloat(row.selling_price || 0).toFixed(2)} <b className="font-extrabold">CFA</b></> },
        user?.role === 'Admin' && { header: 'Cost Price', accessor: 'purchase_price', render: row => <span className="text-gray-500">{parseFloat(row.purchase_price).toFixed(2)} <b className="font-extrabold">CFA</b></span> },
        {
            header: 'Total', accessor: 'total', render: row => {
                const phy = Number(physicalQty[row.id] ?? row.quantity);
                const price = user?.role === 'Admin' ? row.purchase_price : row.selling_price;
                return <>{(phy * parseFloat(price || 0)).toFixed(2)} <b className="font-extrabold">CFA</b></>;
            }
        },
        {
            header: 'Waste',
            accessor: 'waste_quantity',
            render: row => <span className="text-red-600 font-bold">{parseFloat(row.waste_quantity || 0).toFixed(0)}</span>
        },
        {
            header: 'Loss %',
            accessor: 'loss_percent',
            render: row => {
                const sys = Number(row.quantity);
                const phy = Number(physicalQty[row.id] ?? sys);
                const waste = Number(row.waste_quantity || 0);
                const total = phy + waste;
                const percent = total > 0 ? (waste / total) * 100 : 0;
                return (
                    <span className={`text-xs font-black px-2 py-0.5 rounded ${percent > 10 ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'}`}>
                        {percent.toFixed(1)}%
                    </span>
                );
            }
        },
        {
            header: 'Vrai/Faux', accessor: 'vraifaux', render: row => {
                const sys = Number(row.quantity);
                const phy = Number(physicalQty[row.id] ?? sys);
                return <span className={sys === phy ? 'text-green-700 font-bold' : 'text-red-700 font-bold'}>{sys === phy ? 'Vrai' : 'Faux'}</span>;
            }
        },
        {
            header: 'Actions',
            accessor: 'actions',
            render: row => (
                <div className="flex space-x-2">
                    <button onClick={() => setEditStock(row)} title="Edit Stock" className="p-1 hover:bg-gray-100 rounded text-blue-600">📌</button>
                    {user?.role === 'Admin' && (
                        <button onClick={() => handleDeleteStock(row.id)} title="Delete Stock" className="p-1 hover:bg-red-50 rounded text-red-600">🗑️</button>
                    )}
                </div>
            )
        },
    ];

    const totalValue = stocks.reduce((sum, s) => sum + s.quantity * parseFloat(user?.role === 'Admin' ? s.purchase_price : s.selling_price || 0), 0);
    const totalCost = stocks.reduce((sum, s) => sum + s.quantity * parseFloat(s.purchase_price || 0), 0);
    const totalSellPrice = stocks.reduce((sum, s) => sum + s.quantity * parseFloat(s.selling_price || 0), 0);
    const totalStock = stocks.reduce((sum, s) => sum + Number(s.quantity), 0);

    const [wasteLogs, setWasteLogs] = useState([]);
    const [wasteFilterType, setWasteFilterType] = useState('today');
    const [wasteStartDate, setWasteStartDate] = useState('');
    const [wasteEndDate, setWasteEndDate] = useState('');
    const filteredWasteLogs = useMemo(() => {
        if (!wasteLogs) return [];
        const today = new Date();
        if (wasteFilterType === 'today') {
            return wasteLogs.filter(w => {
                const d = new Date(w.logged_at);
                return d.toDateString() === today.toDateString();
            });
        }
        if (wasteFilterType === 'month') {
            return wasteLogs.filter(w => {
                const d = new Date(w.logged_at);
                return d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear();
            });
        }
        if (wasteFilterType === 'range' && wasteStartDate && wasteEndDate) {
            const start = new Date(wasteStartDate);
            const end = new Date(wasteEndDate);
            return wasteLogs.filter(w => {
                const d = new Date(w.logged_at);
                return d >= start && d <= end;
            });
        }
        return wasteLogs;
    }, [wasteLogs, wasteFilterType, wasteStartDate, wasteEndDate]);

    // Waste/Loss total value
    const totalWaste = filteredWasteLogs.reduce((sum, w) => sum + parseFloat(w.loss_cost || 0), 0);
    // Subtotal after waste
    const subTotal = totalValue - totalWaste;
    // 19% deduction from subtotal
    const deduction19 = subTotal * 0.19;
    const subTotalAfterDeduction = subTotal - deduction19;

    // Helper for 19% deduction and subtotal per value
    const calc19 = (val) => val * 0.19;
    const calcSub = (val) => val - calc19(val);

    useEffect(() => {
        fetchStocks();
        // Fetch waste logs for branch and filter
        let url = `/api/waste?depo_id=${branch}`;
        if (wasteFilterType === 'today') {
            url += `&date=${new Date().toISOString().slice(0, 10)}`;
        } else if (wasteFilterType === 'month') {
            const d = new Date();
            url += `&month=${d.getMonth() + 1}&year=${d.getFullYear()}`;
        } else if (wasteFilterType === 'range' && wasteStartDate && wasteEndDate) {
            url += `&start=${wasteStartDate}&end=${wasteEndDate}`;
        }
        authFetch(url)
            .then(res => res.json())
            .then(data => setWasteLogs(Array.isArray(data) ? data : []))
            .catch(() => setWasteLogs([]));
    }, [branch, showPrint, wasteFilterType, wasteStartDate, wasteEndDate]);

    return (
        <div>
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-8 gap-6 bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
                <div className="flex-1">
                    <h2 className="text-3xl font-black text-slate-800 tracking-tight">{t('Warehouse Physical Audit')}</h2>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3 mt-3">
                        <div className="flex items-center bg-slate-50 rounded-full px-4 py-1.5 border border-slate-200">
                            <span className="text-xs font-bold text-slate-500 uppercase tracking-widest mr-2">{t('Audit Target')}:</span>
                            <select
                                value={branch}
                                onChange={e => setBranch(e.target.value)}
                                className="bg-transparent text-indigo-700 text-sm font-black uppercase border-none focus:ring-0 cursor-pointer outline-none"
                            >
                                <option value="Global">🌐 {t('All Branches (Global)')}</option>
                                {depos && depos.map(d => <option key={d.id} value={d.id}>🏢 {d.name}</option>)}
                            </select>
                        </div>
                    </div>
                    <div className="flex items-center gap-4 mt-4 text-sm">
                        <div className="bg-blue-50 text-blue-700 px-3 py-1 rounded-lg border border-blue-100"><span className="text-blue-400 font-medium">{t('Stock')}:</span> <span className="font-bold">{totalStock}</span></div>
                        <div className="bg-red-50 text-red-700 px-3 py-1 rounded-lg border border-red-100"><span className="text-red-400 font-medium">{t('Cost')}:</span> <span className="font-bold">{totalCost.toFixed(2)} CFA</span></div>
                        <div className="bg-emerald-50 text-emerald-700 px-3 py-1 rounded-lg border border-emerald-100"><span className="text-emerald-400 font-medium">{t('Value')}:</span> <span className="font-bold">{totalSellPrice.toFixed(2)} CFA</span></div>
                    </div>
                </div>
                
                <div className="flex flex-wrap gap-2 items-center">
                    {user?.role === 'Admin' && (
                        <button
                            onClick={() => { setIsImportModalOpen(true); setImportResult(null); setImportPreview([]); setImportFile(null); setImportDepoId(''); }}
                            className="group flex items-center justify-center w-10 h-10 bg-white border border-slate-200 hover:border-indigo-400 hover:bg-indigo-50 rounded-xl transition-all shadow-sm"
                            title={t("Import Excel")}
                        >
                            <span className="text-lg group-hover:-translate-y-1 transition-transform">⬆️</span>
                        </button>
                    )}

                    <button 
                        onClick={handleSaveDraft}
                        className="group flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-2.5 px-4 rounded-xl shadow-sm hover:shadow-md transition-all"
                    >
                        <span className="group-hover:scale-110 transition-transform">💾</span> {t('Save Draft')}
                    </button>
                    
                    <button 
                        onClick={handleViewDraft}
                        className="group flex items-center justify-center w-10 h-10 bg-white border border-slate-200 hover:border-yellow-400 hover:bg-yellow-50 rounded-xl transition-all shadow-sm"
                        title={t("View Drafts")}
                    >
                        <span className="text-lg group-hover:rotate-12 transition-transform">📋</span>
                    </button>

                    <button 
                        onClick={handleClearDraft}
                        className="group flex items-center justify-center w-10 h-10 bg-white border border-slate-200 hover:border-red-400 hover:bg-red-50 rounded-xl transition-all shadow-sm"
                        title={t("Clear Draft")}
                    >
                        <span className="text-lg group-hover:scale-110 transition-transform">🗑️</span>
                    </button>

                    <div className="w-px h-8 bg-slate-200 mx-1"></div>

                    <button 
                        onClick={() => setShowPrint(true)}
                        className="group flex items-center justify-center w-10 h-10 bg-white border border-slate-200 hover:border-indigo-400 hover:bg-indigo-50 rounded-xl transition-all shadow-sm"
                        title={t("Print Report")}
                    >
                        <span className="text-lg group-hover:scale-110 transition-transform">🖨️</span>
                    </button>

                    <button 
                        onClick={handleExport}
                        className="group flex items-center justify-center w-10 h-10 bg-white border border-slate-200 hover:border-blue-400 hover:bg-blue-50 rounded-xl transition-all shadow-sm"
                        title={t("Export Excel")}
                    >
                        <span className="text-lg group-hover:translate-y-1 transition-transform">⬇️</span>
                    </button>

                    <div className="w-px h-8 bg-slate-200 mx-1"></div>

                    <button 
                        onClick={() => setIsWasteModalOpen(true)}
                        className="group flex items-center gap-2 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold py-2.5 px-4 rounded-xl shadow-sm hover:shadow-orange-500/30 transition-all"
                    >
                        <span className="group-hover:rotate-12 transition-transform">⚠️</span> {t('Log Waste')}
                    </button>
                </div>
            </div>

            <DataTable columns={columns} data={stocks} rowClassName={row => {
                const sys = Number(row.quantity);
                const phy = Number(physicalQty[row.id] ?? sys);
                return sys === phy ? '' : 'bg-red-50';
            }} />

            {/* ── IMPORT MODAL ─────────────────────────────── */}
            <Modal isOpen={isImportModalOpen} onClose={() => setIsImportModalOpen(false)} title="Import Stock from Excel">
                <div className="space-y-6">
                    {/* Step 1: Download template */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <p className="text-sm text-blue-800 font-semibold mb-1">Step 1 — Download Template</p>
                        <p className="text-xs text-blue-600 mb-3">Your file needs these columns: <code className="bg-white px-1 rounded">name, barcode, sku, quantity, price, category, unit</code></p>
                        <button
                            onClick={handleDownloadTemplate}
                            className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700 transition"
                        >
                            Download Template.xlsx
                        </button>
                    </div>

                    {/* Step 2: Choose Depo */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Step 2 — Destination Depo</label>
                        <select
                            className="w-full border rounded-md p-2 focus:border-indigo-500"
                            value={importDepoId}
                            onChange={e => setImportDepoId(e.target.value)}
                        >
                            <option value="">-- Select target depo --</option>
                            {depos.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                        </select>
                    </div>

                    {/* Step 3: Upload file */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Step 3 — Upload Your Excel File (.xlsx)</label>
                        <div
                            className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-indigo-400 transition"
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <span className="text-4xl">📂</span>
                            <p className="text-gray-500 mt-2 text-sm">{importFile?.file?.name || t('Click to choose or drag & drop an .xlsx file')}</p>
                            <input ref={fileInputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleFileChange} />
                        </div>
                    </div>

                    {/* Preview */}
                    {importPreview.length > 0 && (
                        <div>
                            <p className="text-sm font-semibold text-gray-600 mb-2">{t('Preview')} ({t('first')} {importPreview.length} {t('rows')}):</p>
                            <div className="overflow-x-auto rounded border border-gray-200 text-xs">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>{Object.keys(importPreview[0]).map(k => <th key={k} className="px-3 py-2 text-left text-gray-600 font-semibold">{t(k)}</th>)}</tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {importPreview.map((row, i) => (
                                            <tr key={i}>
                                                {Object.values(row).map((v, j) => <td key={j} className="px-3 py-2 text-gray-700">{String(v)}</td>)}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Result */}
                    {importResult && (
                        <div className={`rounded-lg p-4 ${importResult.imported != null ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'}`}>
                            <p className={`font-bold ${importResult.imported != null ? 'text-emerald-800' : 'text-red-700'}`}>{i18n.exists(importResult.message) ? t(importResult.message) : importResult.message}</p>
                            {importResult.imported != null && (
                                <p className="text-sm mt-1 text-emerald-700">
                                    ✅ {importResult.imported} {t('rows imported')} &nbsp;|&nbsp;
                                    {importResult.skipped > 0 && <span className="text-orange-600">⚠️ {importResult.skipped} {t('skipped')}</span>}
                                </p>
                            )}
                            {importResult.errors?.map((e, i) => <p key={i} className="text-xs text-orange-600 mt-1">{i18n.exists(e) ? t(e) : e}</p>)}
                        </div>
                    )}

                    {/* Submit */}
                    <button
                        onClick={handleImportSubmit}
                        disabled={!importFile || !importDepoId || importLoading}
                        className="w-full bg-indigo-600 text-white py-3 rounded-lg font-bold hover:bg-indigo-700 disabled:opacity-50 transition"
                    >
                        {importLoading ? t('Processing...') : `${t('Import')} ${importFile?.rows?.length ?? 0} ${t('Rows')}`}
                    </button>
                </div>
            </Modal>

            {/* ── WASTE MODAL ──────────────────────────────── */}
            <Modal isOpen={isWasteModalOpen} onClose={() => setIsWasteModalOpen(false)} title={t('Log Physical Loss / Waste')}>
                <form onSubmit={handleLogWaste} className="space-y-6">
                    <div className="bg-orange-50 border-l-4 border-orange-500 p-4 rounded-r-lg">
                        <p className="text-sm font-semibold text-orange-800 flex items-center gap-2">
                            <svg className="w-5 h-5 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                            {t('Log Stock Deterioration')}
                        </p>
                        <p className="text-xs text-orange-700 mt-1 ml-7 opacity-90">{t('Logging waste message')}</p>
                    </div>

                    <div className="space-y-1">
                        <label className="text-xs font-bold text-slate-500 uppercase tracking-wider pl-1">{t('Depo Location')}</label>
                        <select required className="w-full bg-slate-50 border border-slate-200 text-slate-800 rounded-xl p-3 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-all font-semibold" onChange={e => setWasteForm({ ...wasteForm, depo_id: e.target.value })}>
                            <option value="">{t('Select Depo')}</option>
                            {depos.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                        </select>
                    </div>

                    <div className="space-y-1">
                        <label className="text-xs font-bold text-slate-500 uppercase tracking-wider pl-1">{t('Affected Product')}</label>
                        <select required className="w-full bg-slate-50 border border-slate-200 text-slate-800 rounded-xl p-3 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-all font-semibold" onChange={e => setWasteForm({ ...wasteForm, product_id: e.target.value })}>
                            <option value="">{t('Select Product')}</option>
                            {stocks.filter(s => String(s.depo_id) === String(wasteForm.depo_id)).map(s => (
                                <option key={s.id} value={s.product_id}>{s.product_name} ({t('Avail')}: {s.quantity})</option>
                            ))}
                        </select>
                    </div>

                    <div className="grid grid-cols-2 gap-5">
                        <div className="space-y-1">
                            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider pl-1">{t('Quantity Lost')}</label>
                            <input type="number" step="0.01" required placeholder="0.00" className="w-full bg-slate-50 border border-slate-200 text-slate-800 rounded-xl p-3 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-all font-bold text-center" onChange={e => setWasteForm({ ...wasteForm, quantity: e.target.value })} />
                        </div>
                        <div className="space-y-1">
                            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider pl-1">{t('Reason')}</label>
                            <select required className="w-full bg-slate-50 border border-slate-200 text-slate-800 rounded-xl p-3 focus:outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-all font-semibold" onChange={e => setWasteForm({ ...wasteForm, reason: e.target.value })}>
                                <option value="Expired">{t('Expired')}</option>
                                <option value="Broken">{t('Broken')}</option>
                                <option value="Spoiled">{t('Spoiled')}</option>
                                <option value="Stolen">{t('Stolen')}</option>
                            </select>
                        </div>
                    </div>

                    <div className="pt-4 border-t border-slate-100">
                        <button type="submit" className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white py-3.5 px-4 rounded-xl hover:from-orange-600 hover:to-red-600 font-bold shadow-lg shadow-orange-500/30 transition-all active:scale-[0.98] flex items-center justify-center gap-2">
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                            {t('Confirm & Update Stock')}
                        </button>
                    </div>
                </form>
            </Modal>
            <Modal isOpen={!!editStock} onClose={() => setEditStock(null)} title={`${t('Adjust Stock')}: ${editStock?.product_name}`}>
                {editStock && (
                    <form onSubmit={handleUpdateStock} className="space-y-4">
                        <div className="bg-gray-50 p-3 rounded text-sm text-gray-600 mb-4 font-bold uppercase tracking-wider">
                            {t('Depo')}: <strong>{editStock.depo_name}</strong>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">{t('Quantity')}</label>
                            <input type="number" step="0.01" required className="mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2 border"
                                value={editStock.quantity} onChange={e => setEditStock({ ...editStock, quantity: e.target.value })} />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">{t('Expiry Date')}</label>
                            <input type="date" className="mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2 border"
                                value={editStock.expiry_date || ''} onChange={e => setEditStock({ ...editStock, expiry_date: e.target.value })} />
                        </div>
                        <div className="mt-6">
                            <button type="submit" className="w-full bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 font-semibold transition-colors uppercase font-black">
                                {t('Update Stock Record')}
                            </button>
                        </div>
                    </form>
                )}
            </Modal>
            {showDraft && (
                <div className="fixed inset-0 bg-white z-50 p-8 overflow-auto">
                    <div className="flex justify-end mb-4">
                        <button className="bg-gray-200 px-4 py-2 rounded" onClick={() => setShowDraft(false)}>Close</button>
                    </div>
                    <h2 className="text-2xl font-bold mb-4">Draft History for Branch: {(depos.find(d => d.id == branch)?.name || branch)}</h2>
                    {loadingDraftHistory ? (
                        <p>Loading...</p>
                    ) : draftHistory.length === 0 ? (
                        <p className="text-gray-500">No draft history found.</p>
                    ) : (
                        <div className="w-full overflow-x-auto max-w-full">
<table className="min-w-full border text-xs">
                            <thead>
                                <tr className="bg-gray-50">
                                    <th className="p-2">Product</th>
                                    <th className="p-2">Old Qty</th>
                                    <th className="p-2">New Qty</th>
                                    <th className="p-2">Changed By</th>
                                    <th className="p-2">Reason</th>
                                    <th className="p-2">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {draftHistory.map((item, idx) => {
                                    const product = stocks.find(s => String(s.id) === String(item.stock_id));
                                    return (
                                        <tr key={idx} className="border-b">
                                            <td className="p-2">{product?.product_name || item.stock_id}</td>
                                            <td className="p-2">{item.old_physical_qty}</td>
                                            <td className="p-2 font-bold">{item.new_physical_qty}</td>
                                            <td className="p-2">{item.changed_by}</td>
                                            <td className="p-2">{item.change_reason}</td>
                                            <td className="p-2">
                                                {item.status || 'Pending'}
                                                {user?.role === 'Admin' && (!item.status || item.status === 'Pending') && (
                                                    <>
                                                        <button className="ml-2 px-2 py-1 bg-emerald-600 text-white rounded" onClick={async () => {
                                                            // Accept: update stocks in backend
                                                            await authFetch(`/api/stock?action=accept_draft`, {
                                                                method: 'POST',
                                                                body: JSON.stringify({
                                                                    stock_id: item.stock_id,
                                                                    depo_id: branch,
                                                                    new_physical_qty: item.new_physical_qty,
                                                                    draft_id: item.id
                                                                })
                                                            });
                                                            fetchStocks();
                                                            handleViewDraft();
                                                        }}>Accept</button>
                                                        <button className="ml-2 px-2 py-1 bg-red-600 text-white rounded" onClick={async () => {
                                                            // Reject: update draft status only
                                                            await authFetch(`/api/stock?action=reject_draft`, {
                                                                method: 'POST',
                                                                body: JSON.stringify({
                                                                    draft_id: item.id
                                                                })
                                                            });
                                                            handleViewDraft();
                                                        }}>Reject</button>
                                                    </>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
</div>
                    )}
                </div>
            )}


            {/* Hidden Print Auto-Trigger (Used when confirmed from dialog) */}

            {/* Print Date Filter Dialog */}
            {showPrint && (
                <Modal isOpen={showPrint} onClose={() => setShowPrint(false)} title="Print Settings" size="md">
                    <div className="p-6">
                        <h3 className="text-lg font-bold mb-4 text-gray-800">Select Report Date Range</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Date Filter:</label>
                                <select
                                    value={wasteFilterType}
                                    onChange={e => setWasteFilterType(e.target.value)}
                                    className="w-full border rounded-lg px-3 py-2 bg-gray-50 focus:ring-2 focus:ring-indigo-500 outline-none"
                                >
                                    <option value="today">Today</option>
                                    <option value="month">This Month</option>
                                    <option value="all">All Time</option>
                                    <option value="range">Custom Range</option>
                                </select>
                            </div>

                            {wasteFilterType === 'range' && (
                                <div className="flex gap-2 items-center">
                                    <input
                                        type="date"
                                        value={wasteStartDate}
                                        onChange={e => setWasteStartDate(e.target.value)}
                                        className="flex-1 border rounded-lg px-3 py-2 bg-gray-50 text-sm"
                                    />
                                    <span className="text-gray-400 font-bold">to</span>
                                    <input
                                        type="date"
                                        value={wasteEndDate}
                                        onChange={e => setWasteEndDate(e.target.value)}
                                        className="flex-1 border rounded-lg px-3 py-2 bg-gray-50 text-sm"
                                    />
                                </div>
                            )}
                        </div>

                        <div className="mt-8 flex justify-end gap-3">
                            <button
                                onClick={() => setShowPrint(false)}
                                className="px-5 py-2 rounded-lg font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 transition"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    setShowPrint(false);
                                    openInventoryPrintPopup(stocks, depos, branch, physicalQty, wasteFilterType, wasteStartDate, wasteEndDate, filteredWasteLogs);
                                }}
                                className="px-5 py-2 rounded-lg font-bold text-white bg-indigo-600 hover:bg-indigo-700 shadow flex items-center gap-2 transition"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"></path></svg>
                                Continue to Print
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {/* Print Header (Visible ONLY when printing) */}
            <div className="hidden print:block mb-8">
                <style dangerouslySetInnerHTML={{
                    __html: `
                    @media print {
                        @page { size: A4; margin: 10mm; }
                        * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
                        html { zoom: 0.8; }
                        
                        /* Hide app navigation and shell */
                        .print\\:hidden { display: none !important; }
                        
                        /* Ensure the main content spans properly */
                        body { background: white !important; }
                        .print-area { display: block !important; width: 100% !important; }
                        
                        table { page-break-inside: auto; width: 100% !important; table-layout: fixed !important; font-size: 10px !important; }
                        th, td { word-wrap: break-word !important; padding: 4px !important; }
                        tr { page-break-inside: avoid; page-break-after: auto; }
                        thead { display: table-header-group; }
                        tfoot { display: table-footer-group; }
                        .page-break-avoid { page-break-inside: avoid; }
                    }
                `}} />

                <div className="flex items-center justify-between border-b-2 border-indigo-100 pb-4">
                    <div className="flex items-center gap-4">
                        <img src="/logo192.png" alt="Logo" className="w-16 h-16 object-contain" />
                        <div>
                            <div className="text-3xl font-extrabold text-indigo-900 tracking-tight leading-tight">STOCK INVENTORY REPORT</div>
                            <div className="text-sm font-semibold text-gray-400 tracking-widest mt-1">WAREHOUSE MANAGEMENT SYSTEM</div>
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="text-xs font-bold text-gray-500 uppercase tracking-widest">Location Filter</div>
                        <div className="text-xl font-extrabold text-indigo-800">{branch === 'Global' ? 'Consolidated Total' : (depos.find(d => d.id == branch)?.name || branch)}</div>
                        <div className="text-xs text-gray-400 mt-1">Generated on: {new Date().toLocaleString()}</div>
                    </div>
                </div>

                {wasteFilterType !== 'all' && (
                    <div className="mt-4 text-sm font-bold text-gray-600 bg-gray-50 inline-block px-4 py-2 rounded">
                        Reporting Period: {wasteFilterType === 'today' ? 'Today' : wasteFilterType === 'month' ? 'This Month' : `${wasteStartDate} to ${wasteEndDate}`}
                    </div>
                )}
            </div>

            {/* Inventory List Table */}
            <div className="hidden print:block print-container w-full" style={{ background: '#fff', padding: 0, margin: 0 }}>
                <div className="p-4 sm:p-8 print:p-0" style={{ fontFamily: 'Poppins, Arial, sans-serif', minHeight: '100vh' }}>
                    <style dangerouslySetInnerHTML={{
                        __html: `
                                @media print {
                                    table { page-break-inside: auto; }
                                    tr { page-break-inside: avoid; page-break-after: auto; }
                                    thead { display: table-header-group; }
                                    tfoot { display: table-footer-group; }
                                    .page-break-avoid { page-break-inside: avoid; }
                                    @page { size: A4; margin: 10mm; }
                                }
                            `}} />
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
                        <div className="flex items-center gap-4">
                            <img src="/logo192.png" alt="Logo" className="w-16 h-16" style={{ objectFit: 'contain' }} />
                            <div>
                                <div className="text-3xl font-extrabold text-indigo-900 tracking-tight leading-tight">STOCK INVENTORY REPORT</div>
                                <div className="text-sm font-semibold text-gray-400 tracking-widest mt-1">WAREHOUSE MANAGEMENT SYSTEM</div>
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="text-xs font-bold text-gray-500 uppercase tracking-widest">Location Filter</div>
                            <div className="text-xl font-extrabold text-indigo-800">{branch === 'Global' ? 'Consolidated Total' : (depos.find(d => d.id == branch)?.name || branch)}</div>
                            <div className="text-xs text-gray-400 mt-1">Generated on: {new Date().toLocaleString()}</div>
                        </div>
                    </div>
                    <hr className="border-t-2 border-indigo-100 mb-6" />
                    <div className="flex items-center gap-4 mb-6">
                        <label className="font-semibold text-gray-700">Date Filter:</label>
                        <select value={wasteFilterType} onChange={e => setWasteFilterType(e.target.value)} className="border rounded px-2 py-1 focus:ring-2 focus:ring-indigo-200">
                            <option value="today">Today</option>
                            <option value="month">This Month</option>
                            <option value="range">Custom Range</option>
                        </select>
                        {wasteFilterType === 'range' && (
                            <>
                                <input type="date" value={wasteStartDate} onChange={e => setWasteStartDate(e.target.value)} className="border rounded px-2 py-1" />
                                <span>-</span>
                                <input type="date" value={wasteEndDate} onChange={e => setWasteEndDate(e.target.value)} className="border rounded px-2 py-1" />
                            </>
                        )}
                    </div>

                    <div className="w-full overflow-x-auto print:overflow-visible">
                        <table className="w-full text-sm sm:text-base mb-8 bg-white" style={{ borderCollapse: 'separate', borderSpacing: 0, minWidth: '600px' }}>
                            <thead>
                                <tr className="bg-indigo-50 text-indigo-900">
                                    <th className="p-3 font-bold text-left">PRODUCT NAME</th>
                                    <th className="p-3 font-bold text-left">REFERENCE NUMBER</th>
                                    <th className="p-3 font-bold text-left">CATEGORY</th>
                                    <th className="p-3 font-bold text-center">STOCK LEVEL</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(Array.isArray(stocks) ? stocks : []).map(s => (
                                    <tr key={s.id} className="border-b border-indigo-100 hover:bg-indigo-50 transition">
                                        <td className="p-3 font-semibold text-gray-800">{s.product_name}</td>
                                        <td className="p-3 text-gray-400 font-mono">{s.sku || <span className='text-gray-200'>—</span>}</td>
                                        <td className="p-3 text-gray-600">{s.category || <span className='text-gray-200'>—</span>}</td>
                                        <td className="p-3 text-center font-bold" style={{ color: Number(s.quantity) > 0 ? '#3b82f6' : '#ef4444' }}>{s.quantity}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <h3 className="text-lg font-bold mb-2 text-orange-700 mt-8 print:mt-4">Waste / Loss Log</h3>
                    <div className="w-full overflow-x-auto print:overflow-visible page-break-avoid mt-4 print:mt-8">
                        <table className="w-full border text-sm mb-4 bg-white rounded-lg shadow-sm border-gray-200 print:rounded-none print:shadow-none print:border print:border-black print:text-xs" style={{ minWidth: '600px' }}>
                            <thead>
                                <tr className="bg-orange-50 text-orange-900">
                                    <th className="p-3 font-bold">Product</th>
                                    <th className="p-3 font-bold">Qty</th>
                                    <th className="p-3 font-bold">Reason</th>
                                    <th className="p-3 font-bold">Loss Value</th>
                                    <th className="p-3 font-bold">19% Deduct</th>
                                    <th className="p-3 font-bold">Sub Price</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredWasteLogs.length === 0
                                    ? <tr><td colSpan="6" className="p-4 text-center text-gray-400 italic">No waste logged for this branch.</td></tr>
                                    : filteredWasteLogs.map((w, i) => {
                                        const loss = parseFloat(w.loss_cost);
                                        const deduct = calc19(loss);
                                        const sub = calcSub(loss);
                                        return (
                                            <tr key={i} className="border-b hover:bg-orange-50 transition">
                                                <td className="p-3 font-semibold text-gray-800">{w.product_name}</td>
                                                <td className="p-3 text-center">{w.quantity}</td>
                                                <td className="p-3 text-center">{w.reason}</td>
                                                <td className="p-3 text-right">CFA{loss.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                                <td className="p-3 text-right text-red-700">CFA{deduct.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                                <td className="p-3 text-right text-blue-700">CFA{sub.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                            </tr>
                                        );
                                    })}
                            </tbody>
                        </table>
                    </div>
                    <div className="flex justify-end mt-6 page-break-avoid">
                        <div className="bg-indigo-50 rounded shadow p-4 w-full sm:w-80 text-center border-l-4 border-indigo-400">
                            <div className="text-[10px] font-bold text-gray-500 tracking-widest mb-1">TOTAL STOCK VALUE</div>
                            <div className="text-lg font-extrabold text-emerald-700 mb-2">CFA{totalValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                            <div className="text-[10px] font-bold text-gray-500 tracking-widest mb-1">TOTAL WASTE/LOSS VALUE</div>
                            <div className="text-lg font-extrabold text-orange-700 mb-2">CFA{totalWaste.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                            <div className="text-[10px] font-bold text-gray-500 tracking-widest mb-1">SUBTOTAL (AFTER WASTE)</div>
                            <div className="text-lg font-extrabold text-blue-700 mb-2">CFA{subTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                            <div className="text-[10px] font-bold text-gray-500 tracking-widest mb-1">19% DEDUCTION</div>
                            <div className="text-lg font-extrabold text-red-700 mb-2">CFA{deduction19.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                            <div className="text-[10px] font-bold text-gray-500 tracking-widest mb-1">SUBTOTAL AFTER 19% DEDUCTION</div>
                            <div className="text-lg font-extrabold text-indigo-700">CFA{subTotalAfterDeduction.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
export default Inventory;
